/**
 * Employee Form Page (Create/Edit)
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Link,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import {
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useGetRolesQuery,
  useGetRoleQuery,
} from '../../store/api/rbacApi';
import { CreateEmployeeRequest, UpdateEmployeeRequest } from '../../types/rbac';
import { stripRetiredPermissionCodes } from '../../utils/rbacRetiredPermissions';
import { PermissionSelector } from './components/PermissionSelector';
import toast from 'react-hot-toast';

const EmployeeFormPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [formData, setFormData] = useState<CreateEmployeeRequest>({
    email: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    role_id: 0,
    department: '',
    position: '',
    hire_date: new Date().toISOString().split('T')[0],
    custom_permission_codes: [],
    use_only_custom_permissions: false,
    can_access_dashboard: true,
    notes: '',
    send_email: true,
  });

  const { data: employeeDetailData, isLoading: isLoadingDetail } = useGetEmployeeQuery(
    parseInt(id || '0'),
    {
      skip: !id,
    }
  );

  const { data: rolesData } = useGetRolesQuery({ is_active: true });

  const { data: selectedRoleDetail, isFetching: isRoleDetailLoading } = useGetRoleQuery(
    formData.role_id,
    { skip: !formData.role_id }
  );
  const rolePermissionCodes = selectedRoleDetail?.data?.all_permissions;

  const [createEmployee, { isLoading: isCreating, error: createError }] =
    useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating, error: updateError }] =
    useUpdateEmployeeMutation();

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  useEffect(() => {
    if (employeeDetailData?.data) {
      const empDetail = employeeDetailData.data;
      setFormData({
        email: empDetail.user.email,
        phone_number: empDetail.user.phone_number,
        first_name: empDetail.user.first_name,
        last_name: empDetail.user.last_name,
        role_id: empDetail.role.id,
        department: empDetail.department || '',
        position: empDetail.position || '',
        hire_date: empDetail.hire_date,
        custom_permission_codes: stripRetiredPermissionCodes(
          empDetail.custom_permissions.map((p) => p.code)
        ),
        use_only_custom_permissions: empDetail.use_only_custom_permissions ?? false,
        can_access_dashboard: empDetail.can_access_dashboard,
        notes: empDetail.notes || '',
        send_email: false,
      });
    }
  }, [employeeDetailData]);

  // Drop custom permission codes that are not on the selected role when role (or its permissions) changes
  useEffect(() => {
    if (!formData.role_id || !selectedRoleDetail?.data) return;
    const allowed = selectedRoleDetail.data.all_permissions;
    const allowedSet = new Set(allowed);
    setFormData((prev) => {
      const codes = prev.custom_permission_codes ?? [];
      const next = codes.filter((code) => allowedSet.has(code));
      if (next.length === codes.length) return prev;
      return { ...prev, custom_permission_codes: next };
    });
  }, [formData.role_id, selectedRoleDetail?.data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit && id) {
        const updateData: UpdateEmployeeRequest = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone_number: formData.phone_number,
          role_id: formData.role_id,
          department: formData.department,
          position: formData.position,
          custom_permission_codes: stripRetiredPermissionCodes(
            formData.custom_permission_codes || []
          ),
          use_only_custom_permissions: formData.use_only_custom_permissions,
          notes: formData.notes,
          can_access_dashboard: formData.can_access_dashboard,
        };
        await updateEmployee({ id: parseInt(id), ...updateData }).unwrap();
        toast.success(t('rbac.employees.toast.updateSuccess'));
      } else {
        await createEmployee({
          ...formData,
          custom_permission_codes: stripRetiredPermissionCodes(
            formData.custom_permission_codes || []
          ),
        }).unwrap();
        toast.success(t('rbac.employees.toast.createSuccess'));
      }
      navigate('/employees');
    } catch (err: any) {
      console.error('Failed to save employee:', err);
      toast.error(err?.data?.message || (isEdit ? t('rbac.employees.toast.updateError') : t('rbac.employees.toast.createError')));
    }
  };

  const handleChange = (field: keyof CreateEmployeeRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoadingDetail) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          href="/employees"
          onClick={(e) => {
            e.preventDefault();
            navigate('/employees');
          }}
        >
          {t('nav.employees')}
        </Link>
        <Typography color="text.primary">
          {isEdit ? t('rbac.employees.form.breadcrumbEdit') : t('rbac.employees.form.breadcrumbCreate')}
        </Typography>
      </Breadcrumbs>

      <PageHeader
        title={isEdit ? t('rbac.employees.form.editTitle') : t('rbac.employees.form.createTitle')}
        subtitle={
          isEdit
            ? t('rbac.employees.form.editSubtitle')
            : t('rbac.employees.form.createSubtitle')
        }
        action={
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate('/employees')}
          >
            {t('rbac.employees.backToList')}
          </Button>
        }
      />

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {error && (
            <Alert severity="error">
              {(error as any)?.data?.message || t('rbac.employees.toast.saveError')}
            </Alert>
          )}

          {/* Personal Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('rbac.employees.form.personalInfo')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label={t('rbac.employees.form.firstName')}
                    required
                    fullWidth
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                  />

                  <TextField
                    label={t('rbac.employees.form.lastName')}
                    required
                    fullWidth
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                  />
                </Box>

                <TextField
                  label={t('rbac.employees.form.email')}
                  type="email"
                  required
                  fullWidth
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />

                <TextField
                  label={t('rbac.employees.form.phone')}
                  required
                  fullWidth
                  value={formData.phone_number}
                  onChange={(e) => handleChange('phone_number', e.target.value)}
                  helperText={t('rbac.employees.form.phoneHelper')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Role & Department */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('rbac.employees.form.roleDepartment')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>{t('rbac.employees.form.role')}</InputLabel>
                  <Select
                    value={formData.role_id}
                    label={t('rbac.employees.form.role')}
                    onChange={(e) => handleChange('role_id', e.target.value as number)}
                  >
                    <MenuItem value={0} disabled>
                      {t('rbac.employees.form.selectRole')}
                    </MenuItem>
                    {rolesData?.results?.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    label={t('rbac.employees.form.department')}
                    fullWidth
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                  />

                  <TextField
                    label={t('rbac.employees.form.position')}
                    fullWidth
                    value={formData.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                  />
                </Box>

                {!isEdit && (
                  <TextField
                    label={t('rbac.employees.form.hireDate')}
                    type="date"
                    fullWidth
                    value={formData.hire_date}
                    onChange={(e) => handleChange('hire_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}

                <TextField
                  label={t('rbac.employees.form.notes')}
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder={t('rbac.employees.form.notesPlaceholder')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Custom Permissions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('rbac.employees.form.customPermissions')}
              </Typography>
              <FormControlLabel
                sx={{ display: 'block', mb: 1 }}
                control={
                  <Switch
                    checked={formData.use_only_custom_permissions ?? false}
                    onChange={(e) => handleChange('use_only_custom_permissions', e.target.checked)}
                  />
                }
                label={t('rbac.employees.form.useOnlyCustomLabel')}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('rbac.employees.form.useOnlyCustomHelp')}
              </Typography>
              {!formData.role_id ? (
                <Alert severity="info">{t('rbac.employees.form.selectRoleFirst')}</Alert>
              ) : isRoleDetailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <PermissionSelector
                  selectedPermissions={formData.custom_permission_codes || []}
                  onChange={(permissions) => handleChange('custom_permission_codes', permissions)}
                  visiblePermissionCodes={rolePermissionCodes}
                />
              )}
            </CardContent>
          </Card>

          {/* Settings */}
          {!isEdit && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('rbac.employees.form.accountSettings')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.send_email}
                        onChange={(e) => handleChange('send_email', e.target.checked)}
                      />
                    }
                    label={t('rbac.employees.form.sendWelcome')}
                  />
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/employees')}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={
                isLoading ||
                !formData.email ||
                !formData.first_name ||
                !formData.last_name ||
                !formData.role_id
              }
            >
              {isLoading ? t('rbac.employees.form.saving') : isEdit ? t('rbac.employees.form.submitUpdate') : t('rbac.employees.form.submitCreate')}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default EmployeeFormPage;

